import { useEffect, useMemo, useState } from 'react';

import {
	BookAltOutline,
	ChartBarOutline,
	ChatAltOutline,
	DocumentReportOutline,
	GiftOutline,
	HistoryOutline,
	LifeRaftOutline,
	OfficeBuildingOutline,
	PlantOutline,
	PuzzleOutline,
	ShieldCheckOutline,
	UserGroupOutline,
	ViewGridOutline,
} from '@bonusly/design-system';
import { useBuildRecognizeURL } from '@bonusly/url';
import { Permission, ProductTier, useCurrentCompany, useCurrentUser, useFeatureFlag, usePermission } from '@bonusly/who-am-i';

import { useHomepageRedesignEnabled } from '../../../../bonus-feed/features/recognition/components/BonusFeed/hooks/useHomepageRedesignEnabled';
import { AppPath, Route } from '../../../config/router';
import { Variant } from '../../../lib/experiment';
import { adminRewardsPath } from '../../../utils/api';
import { NavConfig, NavLinkConfig, NavSectionConfig } from '../types/nav';
import { filterNavLinks, filterNavSections } from '../utils/filter-nav-items';

export const useNavOptions = () => {
	const user = useCurrentUser();
	const company = useCurrentCompany();
	const recognizeURL = useBuildRecognizeURL();

	// Call all permission and feature flag hooks at the top level for requirements
	const hasOneOnOnesPackage = usePermission(Permission.CanAccessOneOnOnes);
	const hasOneOnOnesLimitedRelease = useFeatureFlag(Variant.OneOnOnesLimitedRelease);
	const hasOneOnOnesEarlyAccess = useFeatureFlag(Variant.OneOnOnesEarlyAccess);
	const canGiveRecognition = usePermission(Permission.CanGiveRecognition);
	const canManagePeerToPeer = usePermission(Permission.CanManagePeerToPeer);
	const canAccessAwards = usePermission(Permission.CanAccessAwards);
	const canAccessNominations = usePermission(Permission.CanAccessNominations);
	const canAccessIncentives = usePermission(Permission.CanAccessIncentives);
	const canAccessAnnouncements = usePermission(Permission.CanAccessAnnouncements);
	const canAccessUserManagement = usePermission(Permission.CanAccessUserManagement);
	const canManageUserImport = usePermission(Permission.CanManageUserImport);
	const canAccessGoals = usePermission(Permission.CanAccessGoals);
	const canAccessAdminCheckInControls = usePermission(Permission.CanAccessAdminCheckInControls);
	const canViewContinuousCheckIns = usePermission(Permission.CanViewContinuousCheckIns);
	const canViewPointsReports = usePermission(Permission.CanViewPointsReports);
	const homepageRedesignEnabled = useHomepageRedesignEnabled();
	const hasAnalyticsTier = company.product_tier === ProductTier.TierTwo || company.product_tier === ProductTier.TierThree;

	// Use state to track pathname so component re-renders on URL change
	const [currentPathname, setCurrentPathname] = useState(window.location.pathname);

	// Listen for URL changes
	useEffect(() => {
		const handleLocationChange = () => {
			setCurrentPathname(window.location.pathname);
		};

		// Check immediately on mount
		handleLocationChange();

		// Listen for popstate events (browser back/forward)
		window.addEventListener('popstate', handleLocationChange);

		// Also listen for pushstate/replacestate (programmatic navigation)
		const originalPushState = window.history.pushState;
		const originalReplaceState = window.history.replaceState;

		window.history.pushState = function (...args) {
			originalPushState.apply(window.history, args);
			handleLocationChange();
		};

		window.history.replaceState = function (...args) {
			originalReplaceState.apply(window.history, args);
			handleLocationChange();
		};

		// Fallback: check periodically in case events are missed
		const intervalId = setInterval(handleLocationChange, 1000);

		return () => {
			window.removeEventListener('popstate', handleLocationChange);
			window.history.pushState = originalPushState;
			window.history.replaceState = originalReplaceState;
			clearInterval(intervalId);
		};
	}, []);

	return useMemo(() => {
		// Computed values for badge counts
		const pendingApprovalsCount = user.pending_awards_count || 0;
		const reportedPostsCount = user.unresolved_bonus_reports_count || 0;
		const recognitionBadgeCount = pendingApprovalsCount + reportedPostsCount;

		// One-on-Ones access logic (consolidated)
		const isOneOnOnesEnabled = hasOneOnOnesEarlyAccess || hasOneOnOnesPackage || hasOneOnOnesLimitedRelease;

		// ===== COMPANY SECTION =====
		const companyLinks: NavLinkConfig[] = [
			{
				requirements: { roles: ['global_admin'] },
				label: 'admin_menu.company.account_settings',
				url: '/admin/company/settings',
			},
			{
				requirements: { roles: ['global_admin', 'tech_admin'] },
				label: 'admin_menu.company.integrations',
				url: recognizeURL(Route.Integrations),
			},
			{
				requirements: { roles: ['global_admin', 'finance_admin'] },
				label: 'admin_menu.company.billing_settings',
				url: recognizeURL(Route.AdminBilling),
			},
			{
				requirements: { roles: ['global_admin', 'finance_admin'] },
				label: 'admin_menu.company.billing_history',
				url: recognizeURL(Route.AdminBillingActivity),
			},
			{
				requirements: { roles: ['global_admin', 'rewards_admin'] },
				matchActive: /\/admin\/rewards/,
				label: 'admin_menu.company.rewards_catalog',
				url: adminRewardsPath(AppPath.Catalog),
			},
			{
				requirements: { roles: ['global_admin'] },
				label: 'admin_menu.company.programs_management',
				url: '/admin/programs/settings',
			},
			{
				requirements: {
					roles: ['global_admin', 'announcements_admin'],
					customCheck: canAccessAnnouncements,
				},
				label: 'admin_menu.company.announcements',
				url: '/admin/company/announcements',
			},
		];

		// ===== USERS SECTION =====
		const userLinks: NavLinkConfig[] = [
			{
				requirements: { roles: ['global_admin'] },
				label: 'admin_menu.users.user_accounts',
				url: recognizeURL(Route.AdminUsers),
			},
			{
				requirements: {
					roles: ['global_admin'],
					customCheck: canAccessUserManagement,
				},
				label: 'admin_menu.users.user_managment',
				url: recognizeURL(company.user_management_integration_url || Route.AdminEmptyIntegration),
			},
			{
				requirements: {
					roles: ['global_admin'],
					customCheck: canManageUserImport,
				},
				label: 'admin_menu.users.manage_user_import',
				url: recognizeURL(Route.AdminImportedUsers),
			},
		];

		// ===== RECOGNITION SECTION =====
		const recognitionLinks: NavLinkConfig[] = [
			{
				requirements: {
					roles: ['global_admin'],
					customCheck: canGiveRecognition || canManagePeerToPeer,
				},
				label: 'admin_menu.recognition.peer_to_peer',
				url: '/admin/programs/peer-to-peer',
			},
			{
				requirements: {
					roles: ['global_admin', 'awards_admin'],
					customCheck: canAccessAwards,
				},
				matchActive: /\/admin\/programs\/awards/,
				label: 'admin_menu.recognition.awards_management',
				url: '/admin/programs/awards',
			},
			{
				requirements: {
					roles: ['global_admin', 'awards_admin'],
					customCheck: canAccessNominations,
				},
				label: 'admin_menu.recognition.nominations',
				matchActive: /\/admin\/programs\/nominations/,
				url: '/admin/programs/nominations',
			},
			{
				requirements: { roles: ['global_admin', 'awards_admin'] },
				label: 'admin_menu.recognition.celebrations',
				matchActive: /\/admin\/programs\/celebrations/,
				url: '/admin/programs/celebrations',
			},
			{
				requirements: {
					roles: ['global_admin', 'awards_admin'],
					customCheck: canAccessIncentives,
				},
				label: 'admin_menu.recognition.incentives',
				matchActive: /\/admin\/programs\/incentives/,
				url: '/admin/programs/incentives',
			},
			{
				badgeCount: pendingApprovalsCount,
				requirements: { roles: ['global_admin', 'awards_admin', 'award_approver'] },
				label: 'admin_menu.recognition.pending',
				url: recognizeURL(Route.AdminAwardsPending),
			},
			{
				badgeCount: reportedPostsCount,
				requirements: { roles: ['global_admin'] },
				label: 'admin_menu.recognition.reported_posts',
				url: recognizeURL(Route.AdminBonusReports),
			},
		];

		// ===== GROWTH (ONE-ON-ONES) SECTION =====
		const oneOnOnesLinks: NavLinkConfig[] = [
			{
				requirements: {
					roles: ['global_admin', 'awards_admin'],
					customCheck: isOneOnOnesEnabled,
				},
				label: 'admin_menu.oneonones.overview',
				matchActive: /one-on-ones\/(?!logs)/,
				url: '/admin/growth/one-on-ones',
			},
			{
				requirements: {
					roles: ['global_admin', 'awards_admin'],
					customCheck: isOneOnOnesEnabled,
				},
				label: 'admin_menu.oneonones.recaps',
				matchActive: /performance-recap/,
				url: '/admin/growth/performance-recap',
			},
			{
				requirements: {
					roles: ['global_admin', 'awards_admin'],
					customCheck: canAccessGoals && isOneOnOnesEnabled,
				},
				label: 'admin_menu.oneonones.company_initiatives',
				matchActive: /company-initiatives/,
				url: '/admin/growth/company-initiatives',
			},
			{
				requirements: {
					roles: ['global_admin', 'awards_admin'],
					customCheck: isOneOnOnesEnabled && user.people_data_admin,
				},
				label: 'admin_menu.oneonones.logs',
				matchActive: /one-on-ones\/logs/,
				url: '/admin/growth/one-on-ones/logs',
			},
			{
				requirements: {
					roles: ['global_admin'],
					customCheck: canAccessAdminCheckInControls,
				},
				label: 'admin_menu.checkin_settings.title',
				matchActive: /pulse-check-settings/,
				url: '/admin/growth/pulse-check-settings',
			},
		];

		// ===== REPORTS SECTION =====
		const reportLinks: NavLinkConfig[] = [
			{
				requirements: { roles: ['global_admin', 'awards_admin', 'finance_admin', 'rewards_admin'] },
				label: 'admin_menu.reports.subscriptions',
				url: '/admin/subscriptions',
			},
			{
				requirements: {
					roles: ['global_admin'],
					customCheck: canGiveRecognition,
				},
				label: 'admin_menu.reports.participationByGroup',
				matchActive: /\/admin\/reports\/participation/,
				url: '/admin/reports/participation',
			},
			{
				requirements: {
					roles: ['global_admin', 'finance_admin'],
					customCheck: hasAnalyticsTier,
				},
				label: 'admin_menu.reports.collaborationMap',
				matchActive: /\/admin\/reports\/collaboration/,
				url: '/admin/reports/collaboration',
			},
			{
				requirements: {
					roles: ['global_admin', 'finance_admin'],
					customCheck: canViewPointsReports,
				},
				label: 'admin_menu.reports.pointsActivity',
				matchActive: /\/admin\/reports\/points-activity/,
				url: '/admin/reports/points-activity',
			},
			{
				requirements: {
					roles: ['global_admin', 'finance_admin'],
					customCheck: canViewContinuousCheckIns,
				},
				label: 'admin_menu.reports.continuousCheckIns',
				url: '/admin/reports/pulse-results',
			},
			{
				requirements: { roles: ['global_admin', 'awards_admin'] },
				label: 'admin_menu.reports.recognition_activity',
				url: recognizeURL(Route.AdminRecognition),
			},
			{
				requirements: { roles: ['global_admin', 'finance_admin'] },
				label: 'admin_menu.reports.recognitionScores',
				url: '/admin/reports/recognition_scores',
			},
			{
				requirements: {
					roles: ['global_admin', 'awards_admin'],
					customCheck: canAccessIncentives,
				},
				label: 'admin_menu.reports.incentives_summary',
				url: '/admin/incentives/summary',
			},
			{
				requirements: { roles: ['global_admin', 'rewards_admin'] },
				label: 'admin_menu.reports.redemptions_activity',
				url: recognizeURL(Route.AdminRewards),
			},
			{
				requirements: { roles: ['global_admin', 'finance_admin'] },
				label: 'admin_menu.reports.rewards_cost',
				url: recognizeURL(Route.AdminTotals),
			},
		];

		// ===== LEGACY SECTION =====
		// Using new subsections structure for better organization
		const legacyAnalyticsLinks: NavLinkConfig[] = [
			{
				label: 'admin_menu.legacy.analytics_leaderboard',
				url: recognizeURL(Route.AnalyticsLeaderboard),
				matchActive: /\/analytics\/leaderboards/,
			},
			{
				label: 'admin_menu.legacy.analytics_participation',
				url: recognizeURL(Route.AnalyticsEngagement),
				matchActive: /\/analytics\/engagement/,
			},
			{
				label: 'admin_menu.legacy.analytics_recognition',
				url: recognizeURL(Route.AnalyticsTotals),
				matchActive: /\/analytics\/bonus_totals/,
			},
			{
				label: 'admin_menu.legacy.analytics_organization_graph',
				url: recognizeURL(Route.AnalyticsOrganization),
				matchActive: /\/analytics\/organization_charts/,
			},
			{
				label: 'admin_menu.legacy.analytics_top_words',
				url: recognizeURL(Route.AnalyticsWords),
				matchActive: /\/analytics\/words/,
			},
		];

		const legacyReportLinks: NavLinkConfig[] = [
			{
				requirements: {
					roles: ['global_admin'],
					customCheck: canGiveRecognition && canViewPointsReports,
				},
				label: 'admin_menu.reports.participation_summary',
				url: recognizeURL(Route.AdminParticipation),
			},
			{
				requirements: {
					roles: ['global_admin', 'finance_admin'],
					customCheck: canGiveRecognition && canViewPointsReports,
				},
				label: 'admin_menu.reports.points_given',
				url: recognizeURL(Route.AdminGiving),
			},
			{
				requirements: {
					roles: ['global_admin', 'finance_admin'],
					customCheck: canViewPointsReports,
				},
				label: 'admin_menu.reports.points_earned',
				url: recognizeURL(Route.AdminEarnings),
			},
		];

		// ===== ADMIN NAV CONFIG =====
		const adminSectionsUnfiltered: NavSectionConfig[] = [
			{
				icon: OfficeBuildingOutline,
				id: 'company-nav',
				title: 'admin_menu.company.title',
				links: companyLinks,
			},
			{
				icon: UserGroupOutline,
				id: 'users-nav',
				title: 'admin_menu.users.title',
				links: userLinks,
			},
			{
				badgeCount: recognitionBadgeCount,
				icon: ChatAltOutline,
				id: 'recognition-nav',
				title: 'admin_menu.recognition.title',
				links: recognitionLinks,
			},
			{
				icon: PlantOutline,
				id: 'one-on-ones-nav',
				title: 'admin_menu.oneonones.title',
				links: oneOnOnesLinks,
			},
			{
				icon: DocumentReportOutline,
				id: 'reports-nav',
				title: 'admin_menu.reports.title',
				links: reportLinks,
			},
			{
				icon: HistoryOutline,
				id: 'legacy-nav',
				title: 'admin_menu.legacy.title',
				subsections: [
					{
						label: 'admin_menu.legacy.analytics',
						links: legacyAnalyticsLinks,
						requirements: {
							roles: ['global_admin'],
							customCheck: homepageRedesignEnabled,
						},
					},
					{
						label: 'admin_menu.legacy.reports',
						links: legacyReportLinks,
					},
				],
			},
		];

		const adminLinksUnfiltered: NavLinkConfig[] = [
			{
				icon: ViewGridOutline,
				requirements: { roles: ['global_admin', 'finance_admin'] },
				label: 'admin_menu.dashboard',
				url: '/admin/dashboard',
			},
		];

		const admin: NavConfig = {
			id: 'admin-nav',
			label: 'admin_menu.menu_label',
			title: 'admin_menu.title',
			links: filterNavLinks(adminLinksUnfiltered, user),
			sections: filterNavSections(adminSectionsUnfiltered, user),
		};

		// Determine which section should be expanded based on the current URL
		const determineActiveSectionIndex = (nav: NavConfig): number | undefined => {
			const pathname = currentPathname;

			for (let i = 0; i < nav.sections.length; i++) {
				const section = nav.sections[i];

				// Helper function to check if a link matches the current pathname
				const linkMatchesPathname = (link: NavLinkConfig): boolean => {
					// For recognize routes, we need to extract the path from the full URL
					let linkPath = link.url;

					// If the link.url contains a full URL (from useBuildRecognizeURL), extract just the path
					if (link.url.includes('http')) {
						try {
							const url = new URL(link.url);
							linkPath = url.pathname;
						} catch (e) {
							// If it's not a valid URL, use the original value
							linkPath = link.url;
						}
					}

					// Check if the current pathname matches the link URL exactly
					if (linkPath === pathname) {
						return true;
					}

					// Check matchActive regex pattern
					if (link.matchActive && link.matchActive.test(pathname)) {
						return true;
					}

					return false;
				};

				// Check direct links
				if (section.links) {
					for (const link of section.links) {
						if (linkMatchesPathname(link)) {
							return i;
						}
					}
				}

				// Check subsection links
				if (section.subsections) {
					for (const subsection of section.subsections) {
						for (const link of subsection.links) {
							if (linkMatchesPathname(link)) {
								return i;
							}
						}
					}
				}
			}

			return undefined; // No active section found
		};

		admin.activeSectionIndex = determineActiveSectionIndex(admin);

		// ===== SUPER ADMIN NAV CONFIG =====
		const superAdminSectionsUnfiltered: NavSectionConfig[] = [
			{
				icon: OfficeBuildingOutline,
				id: 'company-nav',
				title: 'super_admin_menu.companies.title',
				links: [
					{
						label: 'super_admin_menu.companies.manage',
						url: recognizeURL(Route.SuperAdminManageCompanies),
					},
					{
						label: 'super_admin_menu.companies.kyb',
						url: Route.SuperAdminKyb,
					},
					{
						label: 'super_admin_menu.companies.sign_ups',
						url: recognizeURL(Route.SuperAdminCompanySignUps),
					},
					{
						label: 'super_admin_menu.companies.integrations',
						url: recognizeURL(Route.SuperAdminCompanyIntegrations),
					},
				],
			},
			{
				icon: UserGroupOutline,
				id: 'users-nav',
				title: 'super_admin_menu.users.title',
				links: [
					{
						label: 'super_admin_menu.users.manage',
						url: recognizeURL(Route.SuperAdminManageUsers),
					},
					{
						label: 'super_admin_menu.users.alias',
						url: recognizeURL(Route.SuperAdminAliasUsers),
					},
				],
			},
			{
				icon: GiftOutline,
				id: 'rewards-nav',
				title: 'super_admin_menu.rewards.title',
				links: [
					{
						label: 'super_admin_menu.rewards.manage',
						url: recognizeURL(Route.SuperAdminManageRewards),
					},
					{
						label: 'super_admin_menu.rewards.health',
						url: recognizeURL(Route.SuperAdminRewardsHealth),
					},
					{
						label: 'super_admin_menu.rewards.tango_orders',
						url: recognizeURL(Route.SuperAdminTangoOrders),
					},
					{
						label: 'super_admin_menu.rewards.manually_fulfilled',
						url: recognizeURL(Route.SuperAdminManuallyFulfilled),
					},
				],
			},
			{
				icon: BookAltOutline,
				id: 'catalog-nav',
				title: 'super_admin_menu.catalog.title',
				links: [
					{
						label: 'super_admin_menu.catalog.manage',
						url: recognizeURL(Route.SuperAdminManageCatalog),
					},
					{
						label: 'super_admin_menu.catalog.providers',
						url: recognizeURL(Route.SuperAdminProviders),
					},
					{
						label: 'super_admin_menu.catalog.lint',
						url: recognizeURL(Route.SuperAdminLint),
					},
				],
			},
			{
				icon: PuzzleOutline,
				id: 'developer-nav',
				title: 'super_admin_menu.developer.title',
				links: [
					{
						label: 'super_admin_menu.developer.marmon_sftp',
						url: recognizeURL(Route.SuperAdminMarmonSFTP),
					},
					{
						label: 'super_admin_menu.developer.videos',
						url: recognizeURL(Route.SuperAdminVideos),
					},
					{
						label: 'super_admin_menu.developer.internals',
						url: recognizeURL(Route.SuperAdminInternals),
					},
					{
						label: 'super_admin_menu.developer.email',
						url: recognizeURL(Route.SuperAdminEmail),
					},
					{
						label: 'Testing',
						url: recognizeURL(Route.SuperAdminTesting),
					},
				],
			},
			{
				icon: LifeRaftOutline,
				id: 'quality-ops-nav',
				title: 'super_admin_menu.quality_ops.title',
				links: [
					{
						requirements: { roles: ['tech_ops_admin'] },
						label: 'super_admin_menu.quality_ops.everything',
						url: recognizeURL(Route.TechOps),
					},
					{
						requirements: { roles: ['tech_ops_admin'] },
						label: 'super_admin_menu.quality_ops.company_specific',
						url: recognizeURL(Route.TechOpsCompany),
					},
					{
						requirements: { roles: ['tech_ops_admin'] },
						label: 'super_admin_menu.quality_ops.user_specific',
						url: recognizeURL(Route.TechOpsUser),
					},
				],
			},
			{
				icon: ChartBarOutline,
				id: 'kvms-nav',
				title: 'super_admin_menu.kvms.title',
				links: [
					{
						label: 'super_admin_menu.kvms.giving_rate',
						url: Route.SuperAdminRecognitionKVM,
					},
					{
						label: 'super_admin_menu.kvms.one_on_one',
						url: Route.SuperAdminOneOnOneKVM,
					},
					{
						label: 'super_admin_menu.kvms.pulse_checks',
						url: Route.SuperAdminPulseChecksKVM,
					},
					{
						label: 'super_admin_menu.kvms.insights',
						url: Route.SuperAdminInsightsKVM,
					},
					{
						label: 'super_admin_menu.kvms.app_performance',
						url: 'https://app.datadoghq.com/dashboard/xy8-py5-ath?refresh_mode=sliding&live=true',
						external: true,
					},
				],
			},
			{
				icon: ChatAltOutline,
				id: 'bizy-nav',
				title: 'super_admin_menu.bizy.title',
				links: [
					{
						label: 'super_admin_menu.bizy.feedback',
						url: Route.SuperAdminBizyFeedback,
						matchActive: /\/super_admin\/bizy\/feedback/,
					},
					{
						label: 'super_admin_menu.bizy.conversations',
						url: Route.SuperAdminBizyConversations,
						matchActive: /\/super_admin\/bizy\/conversations/,
					},
					{
						label: 'super_admin_menu.bizy.metrics',
						url: Route.SuperAdminBizyKVM,
						matchActive: /\/super_admin\/kvms\/bizy/,
					},
				],
			},
			{
				icon: PlantOutline,
				id: 'habits-bu-nav',
				title: 'super_admin_menu.habits_bu.title',
				links: [
					{
						label: 'super_admin_menu.habits_bu.trial_conversion',
						url: Route.SuperAdminTrialConversion,
						matchActive: /\/super_admin\/kvms\/trial-conversion/,
					},
					{
						label: 'super_admin_menu.habits_bu.one_on_one_activation',
						url: Route.SuperAdminOneOnOneActivation,
						matchActive: /\/super_admin\/kvms\/one-on-one-activation/,
					},
					{
						label: 'super_admin_menu.habits_bu.transcript_metrics',
						url: Route.SuperAdminTranscriptMetrics,
						matchActive: /\/super_admin\/kvms\/transcript-metrics/,
					},
				],
			},
			{
				icon: ShieldCheckOutline,
				id: 'security-nav',
				title: 'Security',
				links: [
					{
						label: 'Blocklisted Companies',
						url: recognizeURL(Route.SuperAdminBlocklistedCompanies),
					},
					{
						label: 'Blocklisted IP Addresses',
						url: recognizeURL(Route.SuperAdminBlocklistedIpAddresses),
					},
					{
						label: 'Blocklisted Email Domains',
						url: recognizeURL(Route.SuperAdminBlocklistedEmailDomains),
					},
					{
						label: 'Passlisted IP Addresses',
						url: recognizeURL(Route.SuperAdminPasslistedIpAddresses),
					},
				],
			},
		];

		// Filter super admin sections based on user role
		const hasSuperAdminRole = user.show_superadmin_feature;
		const superAdmin: NavConfig = {
			id: 'super-admin-nav',
			label: 'super_admin_menu.menu_label',
			title: 'super_admin_menu.title',
			roles: ['show_superadmin_feature'],
			sections: hasSuperAdminRole ? filterNavSections(superAdminSectionsUnfiltered, user) : [],
		};

		superAdmin.activeSectionIndex = determineActiveSectionIndex(superAdmin);

		return { admin, superAdmin };
	}, [
		company,
		user,
		recognizeURL,
		canAccessAdminCheckInControls,
		canAccessAnnouncements,
		canAccessAwards,
		canAccessGoals,
		canAccessIncentives,
		canAccessNominations,
		canAccessUserManagement,
		canGiveRecognition,
		canManagePeerToPeer,
		canManageUserImport,
		canViewContinuousCheckIns,
		canViewPointsReports,
		currentPathname,
		hasAnalyticsTier,
		hasOneOnOnesEarlyAccess,
		hasOneOnOnesLimitedRelease,
		hasOneOnOnesPackage,
		homepageRedesignEnabled,
	]);
};
